import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Sparkles } from 'lucide-react';
import { usePlayerDrilldown } from '../components/PlayerDrilldownContext';
import { useStaffAlerts } from './StaffAlertsContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_INTERVAL_MS = 15_000;
const MUTE_KEY = 'square_receipts_chime_muted';

const SquareReceiptsContext = createContext({
    rows: [],
    count: 0,
    loading: true,
    flash: false,
    muted: false,
    refresh: () => {},
    toggleMute: () => {},
    pendingOpenId: null,
    openReview: () => {},
    clearOpenReview: () => {},
    optimisticRemove: () => {},
});

export const useSquareReceipts = () => useContext(SquareReceiptsContext);

/**
 * One source of truth for the pending Square Receipts queue across the
 * cashier / admin dashboard. Polls every 15s, plays a chime + flashes
 * subscribed surfaces (queue card + header badge) when new ids appear.
 *
 * Designed so both the in-tab card and the floating header badge consume
 * the same data — no duplicate fetches, single chime per new arrival.
 */
export const SquareReceiptsProvider = ({ children, enabled = true }) => {
    const { openPlayer } = usePlayerDrilldown();
    const { push: pushAlert } = useStaffAlerts();
    const [rows, setRows] = useState([]);
    const [auditCount, setAuditCount] = useState(0);   // # of auto-credited receipts awaiting admin audit
    const [loading, setLoading] = useState(true);
    const [flash, setFlash] = useState(false);
    const [muted, setMuted] = useState(() => {
        try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
    });
    const knownIds = useRef(null);     // null = first load (don't chime)
    const audioCtx = useRef(null);
    const mutedRef = useRef(muted);
    useEffect(() => { mutedRef.current = muted; }, [muted]);

    // ── One-tap "open the current receipt" wiring ─────────────────────────
    // When a new receipt lands, the toast (or the floating green badge) can
    // call openReview(id) to ask the queue card to pop its review dialog
    // open for that receipt immediately — saves a scroll + tap on small
    // screens where the queue is below the fold.
    //
    // We store {id, nonce} instead of a bare id so that re-opening the SAME
    // receipt right after closing it (Escape → same badge click) still
    // triggers the QueueCard's useEffect — setPendingOpenId(sameValue) alone
    // wouldn't cause a re-render.
    const [pendingOpen, setPendingOpen] = useState(null);
    const pendingOpenId = pendingOpen?.id || null;
    const openReview = useCallback((id) => {
        if (!id) return;
        setPendingOpen({ id: String(id), nonce: Date.now() });
        try {
            const el = document.getElementById('square-receipts');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch { /* ignore */ }
    }, []);
    const clearOpenReview = useCallback(() => setPendingOpen(null), []);

    // Optimistic queue removal — when a cashier approves or rejects a receipt
    // the API returns instantly but the polling refresh is on a 15s interval.
    // To make the row vanish from the dashboard the moment the cashier hits
    // the button, we drop it from local state synchronously AND fire a
    // background refresh to reconcile with the server.
    const optimisticRemove = useCallback((id) => {
        if (!id) return;
        setRows((prev) => prev.filter((r) => String(r.id) !== String(id)));
        if (knownIds.current && knownIds.current.has) {
            knownIds.current.delete(String(id));
        }
    }, []);

    const toggleMute = useCallback(() => {
        setMuted((m) => {
            const next = !m;
            try { localStorage.setItem(MUTE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
            return next;
        });
    }, []);

    // Tiny WebAudio "ding" — two-note chime, ~250ms. No external file needed.
    const playChime = useCallback(() => {
        if (mutedRef.current) return;
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            if (!audioCtx.current) audioCtx.current = new Ctx();
            const ctx = audioCtx.current;
            if (ctx.state === 'suspended') ctx.resume();
            const now = ctx.currentTime;
            [880, 1320].forEach((freq, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = freq;
                const start = now + i * 0.12;
                gain.gain.setValueAtTime(0.0001, start);
                gain.gain.exponentialRampToValueAtTime(0.28, start + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.22);
                osc.connect(gain).connect(ctx.destination);
                osc.start(start);
                osc.stop(start + 0.25);
            });
        } catch { /* autoplay might be blocked until first interaction */ }
    }, []);

    const fetchRows = useCallback(async (opts = {}) => {
        if (!enabled) return;
        const { quiet = false } = opts;
        if (!quiet) setLoading(true);
        try {
            const { data } = await axios.get(`${API}/cashier/square-receipts/pending`,
                { withCredentials: true });
            const next = data.rows || [];
            const nextIds = new Set(next.map((r) => r.id));
            if (knownIds.current !== null) {
                const fresh = next.filter((r) => !knownIds.current.has(r.id));
                if (fresh.length > 0) {
                    playChime();
                    setFlash(true);
                    setTimeout(() => setFlash(false), 1800);
                    if (fresh.length === 1) {
                        const r = fresh[0];
                        const amt = (r.ocr_amount || r.claimed_amount || 0).toFixed(2);
                        toast.success(`📷 New receipt — ${r.player_name} · $${amt}`, {
                            duration: 12000,
                            action: {
                                label: 'Review',
                                onClick: () => openReview(r.id),
                            },
                        });
                        pushAlert({
                            id: `receipt-${r.id}`,
                            level: 'info',
                            icon: '📷',
                            title: `New receipt — ${r.player_name} · $${amt}`,
                            description: 'Pending cashier review.',
                            userId: r.user_id,
                        });
                    } else {
                        const newest = fresh[0];
                        toast.success(`📷 ${fresh.length} new receipts to verify`, {
                            duration: 12000,
                            action: newest?.id ? {
                                label: 'Open newest',
                                onClick: () => openReview(newest.id),
                            } : undefined,
                        });
                        pushAlert({
                            id: `receipt-batch-${Date.now()}`,
                            level: 'info',
                            icon: '📷',
                            title: `${fresh.length} new receipts to verify`,
                            description: 'See the Payment Receipts queue.',
                        });
                    }
                }
            }
            knownIds.current = nextIds;
            setRows(next);
        } catch { /* silent — fork agent may not be staff */ }
        if (!quiet) setLoading(false);
    }, [enabled, playChime, pushAlert, openReview]);

    // ── Auto-credit live toasts (24/7 mode only) ────────────────────────
    // When admin has flipped `always_on=true`, the system is auto-crediting
    // receipts in the background while the cashier is at the counter. Poll
    // for those and toast each new one so a wallet doesn't silently jump
    // out from under the cashier. Silenced when 24/7 is OFF.
    const knownAutoIds = useRef(null);
    const fetchAutoCredits = useCallback(async () => {
        if (!enabled) return;
        try {
            // Cheap toggle check; bail before hitting the receipts list when off.
            const status = await axios.get(`${API}/cashier/auto-credit-status`,
                { withCredentials: true });
            if (!status.data?.always_on) {
                // When 24/7 is off, reset baseline so re-enabling later won't
                // dump a backlog of historical auto-credits as toasts.
                knownAutoIds.current = null;
                return;
            }
            const { data } = await axios.get(`${API}/cashier/square-receipts/auto-credited-recent`,
                { params: { minutes: 5 }, withCredentials: true });
            const rows = data.rows || [];
            const ids = new Set(rows.map((r) => r.id));
            if (knownAutoIds.current !== null) {
                const fresh = rows.filter((r) => !knownAutoIds.current.has(r.id));
                fresh.forEach((r) => {
                    const tag = [r.player_name, r.player_last4].filter(Boolean).join(' · ');
                    const amt = Number(r.credit_amount || r.ocr_amount || 0).toFixed(2);
                    playChime();
                    toast.success(`✨ Auto-credited $${amt} to ${tag || 'a player'}`, {
                        description: 'Tap to verify in the player\u2019s drilldown.',
                        duration: 9000,
                        action: r.user_id ? {
                            label: 'View',
                            onClick: () => openPlayer(r.user_id),
                        } : undefined,
                    });
                    pushAlert({
                        id: `auto-${r.id}`,
                        level: 'success',
                        icon: '✨',
                        title: `Auto-credited $${amt} — ${tag || 'a player'}`,
                        description: 'Wallet credited in background.',
                        userId: r.user_id,
                    });
                });
            }
            knownAutoIds.current = ids;
        } catch { /* silent */ }
    }, [enabled, playChime, openPlayer, pushAlert]);

    // ── Audit-queue count (admin only) ──────────────────────────────────
    // Auto-credited receipts still flagged `audit_required=True` show up
    // in the After-hours audit card. Polling the count here lets the
    // floating badge announce "N auto-loads awaiting audit" so the admin
    // doesn't have to remember to visit the Reports tab. Cashiers can't
    // hit this endpoint (403) — we silently ignore and skip the badge.
    const knownAuditIds = useRef(null);
    const fetchAuditCount = useCallback(async () => {
        if (!enabled) return;
        try {
            const { data } = await axios.get(
                `${API}/admin/square-receipts/auto-credited?audit_only=true&images=false`,
                { withCredentials: true },
            );
            const list = data.rows || [];
            const nextIds = new Set(list.map((r) => r.id));
            // Chime once when new items land (skips the first fetch to avoid
            // firing on page load with existing backlog).
            if (knownAuditIds.current !== null) {
                const fresh = list.filter((r) => !knownAuditIds.current.has(r.id));
                if (fresh.length > 0) {
                    playChime();
                    setFlash(true);
                    setTimeout(() => setFlash(false), 1800);
                }
            }
            knownAuditIds.current = nextIds;
            setAuditCount(list.length);
        } catch { /* 403 for non-admins → skip */ }
    }, [enabled, playChime]);

    useEffect(() => {
        if (!enabled) return;
        fetchRows();
        fetchAutoCredits();
        fetchAuditCount();
        const id = setInterval(() => {
            fetchRows({ quiet: true });
            fetchAutoCredits();
            fetchAuditCount();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [enabled, fetchRows, fetchAutoCredits, fetchAuditCount]);

    return (
        <SquareReceiptsContext.Provider value={{
            rows,
            count: rows.length,
            auditCount,
            refreshAudit: fetchAuditCount,
            loading,
            flash,
            muted,
            refresh: fetchRows,
            toggleMute,
            pendingOpen,
            pendingOpenId,
            openReview,
            clearOpenReview,
            optimisticRemove,
        }}>
            {children}
            {/* Floating receipts + auto-loads pills removed per operator request
                (Jun 2026) — reconciled through other channels. Components kept
                for reference. */}
            {false && <FloatingReceiptsBadge />}
            {false && <FloatingAuditBadge />}
        </SquareReceiptsContext.Provider>
    );
};

/**
 * Auto-rendered floating pill on the bottom-right of every staff page that
 * lives inside the provider. Always visible so the admin has one-tap
 * access to the receipt-audit queue — even when the queue is empty (user
 * feedback 2026-07-23: "the little green thing is off my admin panel").
 *
 * Idle state (0 pending): small, translucent, low-attention.
 * Active state (>=1 pending): bright green, animated dot, count badge.
 * Flash state (new arrival): scales up + pulses for 1.8s.
 */
const FloatingReceiptsBadge = () => {
    const { count, flash, rows, openReview } = useSquareReceipts();

    const goToQueue = () => {
        // Tap to immediately open the freshest receipt review dialog when
        // there is one — that's why the admin hit the badge. When the
        // queue is empty, we just switch to the Reports tab so the admin
        // can scroll history / reconcile older receipts.
        const newest = count > 0 && rows && rows.length > 0 ? rows[rows.length - 1] : null;
        // Fire the tab-switch event FIRST so AdminDashboard can render the
        // Reports tab (which mounts SquareReceiptsQueueCard). The queue card
        // isn't in the DOM until this tick completes.
        try {
            window.dispatchEvent(new CustomEvent('betohalics:open-receipts-tab', {
                detail: { id: newest?.id || null },
            }));
        } catch { /* ignore */ }
        if (newest?.id) {
            // openReview stashes {id, nonce} in context; when the QueueCard
            // mounts it'll auto-open the review dialog.
            openReview(newest.id);
            return;
        }
        // Queue empty — poll the DOM until the just-mounted card appears,
        // then smooth-scroll to it. Bug reported 2026-07-23: on Production
        // the sync scrollIntoView fired BEFORE the Reports tab mounted so
        // nothing happened and the admin never got moved to the queue.
        let attempts = 0;
        const tryScroll = () => {
            attempts += 1;
            const el = document.getElementById('square-receipts');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else if (attempts < 10) {
                setTimeout(tryScroll, 120);
            }
        };
        setTimeout(tryScroll, 60);
    };

    const idle = count === 0;

    return (
        <button
            type="button"
            onClick={goToQueue}
            className={`fixed z-40 bottom-6 right-4 inline-flex items-center gap-2 rounded-full font-black uppercase tracking-wider border shadow-lg backdrop-blur transition-all ${
                idle
                    ? 'px-2.5 py-1.5 text-[10px] bg-[#39FF14]/8 text-[#39FF14]/70 border-[#39FF14]/25 hover:bg-[#39FF14]/15 hover:text-[#39FF14] hover:border-[#39FF14]/60'
                    : flash
                        ? 'px-3.5 py-2.5 text-sm bg-[#39FF14] text-zinc-900 border-[#39FF14] scale-110 square-receipts-flash'
                        : 'px-3.5 py-2.5 text-sm bg-[#39FF14]/15 text-[#39FF14] border-[#39FF14]/50 hover:bg-[#39FF14]/25 hover:border-[#39FF14]'
            }`}
            title={idle
                ? 'Receipt review inbox — 0 pending. Tap to open reconciliation.'
                : `${count} Square receipt${count === 1 ? '' : 's'} pending review — click to review`}
            aria-label={idle ? 'Receipt review inbox' : 'Pending Square receipts'}
            data-testid="square-receipts-floating-badge"
            style={{ paddingBottom: `max(${idle ? '0.375rem' : '0.625rem'}, env(safe-area-inset-bottom))` }}
        >
            <Camera className={idle ? 'w-3 h-3' : 'w-4 h-4'} />
            <span>
                {idle
                    ? 'Receipts'
                    : (<>{count} receipt{count === 1 ? '' : 's'}</>)}
            </span>
            <span className={`relative flex ${idle ? 'h-1.5 w-1.5' : 'h-2.5 w-2.5'}`} aria-hidden="true">
                <span className={`absolute inline-flex h-full w-full rounded-full bg-[#39FF14] ${idle ? 'opacity-30' : 'opacity-75'} ${flash ? 'animate-ping' : ''}`} />
                <span className={`relative inline-flex rounded-full bg-[#39FF14] ${idle ? 'h-1.5 w-1.5 opacity-60' : 'h-2.5 w-2.5'}`} />
            </span>
        </button>
    );
};

/**
 * Second floating pill (positioned above the pending one) that surfaces
 * auto-credited receipts still awaiting an admin sign-off in the After-hours
 * auto-credit card. Only appears when at least one is un-audited. Tapping it
 * jumps to Reports > After-hours auto-credit for one-click OK / clawback.
 *
 * Admin-only in practice — the underlying endpoint 403s for cashiers, so
 * `auditCount` stays 0 and the pill never mounts.
 */
const FloatingAuditBadge = () => {
    const { auditCount, flash } = useSquareReceipts();
    if (!auditCount) return null;

    const goToAudit = () => {
        try {
            window.dispatchEvent(new CustomEvent('betohalics:open-receipts-tab', {
                detail: { id: null },
            }));
        } catch { /* ignore */ }
        // Poll for the target card since the Reports tab may not have
        // mounted yet when this fires (same fix pattern as goToQueue).
        let attempts = 0;
        const tryScroll = () => {
            attempts += 1;
            const el = document.getElementById('after-hours-auto-credit');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else if (attempts < 10) {
                setTimeout(tryScroll, 120);
            }
        };
        setTimeout(tryScroll, 60);
    };

    return (
        <button
            type="button"
            onClick={goToAudit}
            className={`fixed z-40 right-4 inline-flex items-center gap-2 px-3.5 py-2.5 rounded-full text-sm font-black uppercase tracking-wider border shadow-lg backdrop-blur transition-all ${
                flash
                    ? 'bg-[#FFD700] text-zinc-900 border-[#FFD700] scale-110'
                    : 'bg-[#FFD700]/15 text-[#FFD700] border-[#FFD700]/50 hover:bg-[#FFD700]/25 hover:border-[#FFD700]'
            }`}
            title={`${auditCount} auto-loaded receipt${auditCount === 1 ? '' : 's'} awaiting audit — click to review`}
            aria-label="Auto-loads awaiting audit"
            data-testid="auto-audit-floating-badge"
            style={{ bottom: 'calc(4.75rem + env(safe-area-inset-bottom))' }}
        >
            <Sparkles className="w-4 h-4" />
            <span>{auditCount} auto-load{auditCount === 1 ? '' : 's'}</span>
            <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
                <span className={`absolute inline-flex h-full w-full rounded-full bg-[#FFD700] opacity-75 ${flash ? 'animate-ping' : ''}`} />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FFD700]" />
            </span>
        </button>
    );
};

export default SquareReceiptsProvider;